import { Link } from 'react-router-dom';
import { Play, Zap, AlertTriangle, ChevronDown } from 'lucide-react';
import type { CreativeBriefSummary } from './types';

interface CreativeRecord {
  creative_id: string;
  source?: string;
  status?: string;
  platform?: string;
  form?: {
    headline?: string;
    media_type?: string;
    thumbnail_url?: string;
    image_url?: string;
  };
  brief_id?: string | null;
  brief_version?: number | null;
  angle_id?: string | null;
}

interface Props {
  creative: CreativeRecord;
  brief: CreativeBriefSummary | null;
  onRunReview: (personaId?: string) => void;
  onIterate: () => void;
  onBackfillBrief: () => void;
  iterateEnabled?: boolean;
}

const STATUS_COLOR: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  draft: 'bg-slate-100 text-slate-600',
  completed: 'bg-blue-100 text-blue-700',
  archived: 'bg-slate-100 text-slate-400',
  enriched: 'bg-emerald-100 text-emerald-700',
  extracted: 'bg-blue-100 text-blue-700',
  fetched: 'bg-slate-100 text-slate-600',
  discovered: 'bg-slate-100 text-slate-600',
  failed: 'bg-rose-100 text-rose-700',
};

type BriefLinkState = 'linked_current' | 'linked_outdated' | 'unlinked';

function getBriefLinkState(
  creative: CreativeRecord,
  brief: CreativeBriefSummary | null,
): BriefLinkState {
  if (!creative.brief_id) return 'unlinked';
  if (!brief) return 'linked_current';
  return brief.version < brief.latest_version ? 'linked_outdated' : 'linked_current';
}

export function CreativeIdentityBar({
  creative,
  brief,
  onRunReview,
  onIterate,
  onBackfillBrief,
  iterateEnabled = false,
}: Props) {
  const form = creative.form ?? {};
  const thumbnail = form.thumbnail_url ?? form.image_url;
  const title = form.headline || creative.creative_id;
  const status = (creative.status ?? 'draft') as string;
  const briefLinkState = getBriefLinkState(creative, brief);

  return (
    <div
      className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4"
      style={{ height: 72 }}
    >
      {thumbnail && /^https?:/i.test(thumbnail) ? (
        <img
          src={thumbnail}
          alt=""
          className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-slate-200"
        />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-slate-100 flex-shrink-0 flex items-center justify-center">
          <Play size={16} className="text-slate-400" />
        </div>
      )}

      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-sm font-bold text-navy-900 truncate max-w-[360px]">{title}</h1>
          <span
            className={`inline-flex items-center text-[10px] font-mono px-2 py-0.5 rounded-full uppercase ${
              STATUS_COLOR[status] ?? STATUS_COLOR.draft
            }`}
          >
            {status}
          </span>
          {creative.platform && creative.platform !== 'unknown' && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 text-slate-500">
              {creative.platform}
            </span>
          )}
          {form.media_type && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 text-slate-500">
              {form.media_type}
            </span>
          )}
          {creative.source && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 text-slate-400">
              {creative.source}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs">
          {briefLinkState === 'linked_current' && (
            <Link
              to={`/creative/briefs/${brief?.brief_id ?? creative.brief_id}`}
              className="flex items-center gap-1 text-slate-500 hover:text-teal-600 transition-colors"
            >
              <span className="text-slate-400">↳</span>
              <span className="hover:underline">{brief?.title ?? 'Brief'}</span>
              {brief && (
                <span className="font-mono text-slate-400">v{brief.version}</span>
              )}
            </Link>
          )}
          {briefLinkState === 'linked_outdated' && brief && (
            <div className="flex items-center gap-2">
              <Link
                to={`/creative/briefs/${brief.brief_id}`}
                className="flex items-center gap-1 text-slate-500 hover:text-teal-600 transition-colors"
              >
                <span className="text-slate-400">↳</span>
                <span className="hover:underline">{brief.title}</span>
                <span className="font-mono text-slate-400">v{brief.version}</span>
              </Link>
              <span className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                <AlertTriangle size={9} />
                Brief updated → v{brief.latest_version}
              </span>
            </div>
          )}
          {briefLinkState === 'unlinked' && (
            <button
              onClick={onBackfillBrief}
              className="text-[11px] font-mono text-slate-400 hover:text-teal-600 border border-dashed border-slate-200 hover:border-teal-300 rounded px-2 py-0.5 transition-colors"
            >
              + Link to brief
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => onRunReview()}
          className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-teal-600 border border-slate-200 hover:border-teal-300 px-3 py-1.5 rounded-lg transition-colors"
        >
          ✦ Run Review
          <ChevronDown size={11} className="text-slate-400" />
        </button>
        <button
          onClick={onIterate}
          disabled={!iterateEnabled}
          className="inline-flex items-center gap-1.5 text-xs bg-teal-500 hover:bg-teal-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
        >
          <Zap size={12} />
          Iterate
        </button>
      </div>
    </div>
  );
}
